import { el } from '../../core/dom.js';

/**
 * Document upload, matching the classic onboarding drop zone:
 * the same types and 40 MB limit, one file at a time, with progress.
 */

export const KB_MAX_BYTES = 40 * 1024 * 1024;

export const KB_FILE_EXTS = [
  '.md', '.txt', '.csv', '.json', '.html', '.pdf', '.docx',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.ico',
  '.tif', '.tiff', '.heic', '.heif', '.avif', '.apng',
];

export const KB_FILE_ACCEPT = `${KB_FILE_EXTS.join(',')},image/*`;

/** @param {File} file */
export function kbFileAllowed(file) {
  const mime = (file && file.type) || '';
  if (mime.startsWith('audio/') || mime.startsWith('video/')) return false;
  if (mime.startsWith('image/')) return true;
  const name = (file && file.name) || '';
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  return KB_FILE_EXTS.includes(ext);
}

/** @param {object} [doc] */
export function readBadge(doc) {
  const key = doc && doc.read_badge;
  if (key === 'stored_linked') return { label: 'Stored + linked', tone: 'linked' };
  if (key === 'stored_only') return { label: 'Stored only', tone: 'stored' };
  return { label: 'Read + searchable', tone: 'read' };
}

function token() {
  try { return localStorage.getItem('accessToken') || ''; } catch { return ''; }
}

function uploadError(xhr, data) {
  if (data && data.error) return data.error;
  if (xhr && xhr.status === 413) return 'File too large (max 40 MB)';
  return 'Upload failed';
}

/**
 * @param {File} file
 * @param {string} productId
 * @param {{ onProgress?: (pct: number) => void, onIngest?: () => void }} [hooks]
 */
export function uploadDocument(file, productId, hooks) {
  const body = new FormData();
  body.append('file', file);
  body.append('product_id', productId);
  const path = `/api/v1/documents/upload?product_id=${encodeURIComponent(productId)}`;
  const notify = hooks || {};
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    const access = token();
    if (access) xhr.setRequestHeader('Authorization', `Bearer ${access}`);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !notify.onProgress) return;
      notify.onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };
    xhr.upload.onload = () => { if (notify.onIngest) notify.onIngest(); };
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText || ''); } catch { data = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data || {});
      else resolve({ error: uploadError(xhr, data) });
    };
    xhr.onerror = () => resolve({ error: 'Upload failed' });
    xhr.send(body);
  });
}

function jobMeta(job) {
  if (job.phase === 'upload') return `Uploading ${job.pct || 0}%`;
  if (job.phase === 'ingest') return 'Ingesting';
  if (job.phase === 'error') return job.error || 'Upload failed';
  if (job.phase === 'queued') return 'Waiting';
  return (job.doc && (job.doc.user_summary || job.doc.file_type)) || '';
}

function jobBadge(job) {
  if (job.phase === 'done') return readBadge(job.doc).label;
  if (job.phase === 'upload') return `${job.pct || 0}%`;
  if (job.phase === 'error') return 'Failed';
  if (job.phase === 'ingest') return 'Ingesting';
  return 'Waiting';
}

function jobRow(job, onRetry) {
  const tone = job.phase === 'done' ? readBadge(job.doc).tone : job.phase;
  return el('div', { class: 'kb-file-row' },
    el('div', { class: 'kb-file-copy' },
      el('div', { class: 'kb-file-name' }, job.name || 'File'),
      el('div', { class: 'kb-file-meta' }, jobMeta(job))),
    job.phase === 'error'
      ? el('button', { class: 'kb-btn', type: 'button', onClick: () => onRetry(job.key) }, 'Retry')
      : el('span', { class: `kb-badge is-${tone}` }, jobBadge(job)));
}

/**
 * @param {{ jobs: object[], onFiles: (files: File[]) => void, onRetry: (key: string) => void }} opts
 */
export function renderDropZone(opts) {
  const input = el('input', {
    id: 'kb-file',
    class: 'kb-file',
    type: 'file',
    multiple: 'true',
    accept: KB_FILE_ACCEPT,
    onChange() {
      const picked = [...(input.files || [])];
      input.value = '';
      opts.onFiles(picked);
    },
  });
  const drop = el('label', {
    id: 'kb-drop',
    class: 'kb-drop',
    for: 'kb-file',
    onDragover(event) {
      event.preventDefault();
      drop.classList.add('is-over');
    },
    onDragleave() { drop.classList.remove('is-over'); },
    onDrop(event) {
      event.preventDefault();
      drop.classList.remove('is-over');
      opts.onFiles([...(event.dataTransfer && event.dataTransfer.files || [])]);
    },
  },
    el('span', { class: 'kb-drop-title' }, 'Drop files here'),
    el('span', { class: 'kb-drop-sub' }, 'md · txt · pdf · docx · images — up to 40 MB each'));
  const rows = (opts.jobs || []).map((job) => jobRow(job, opts.onRetry));
  return el('section', { class: 'kb-uploads kb-glass' }, drop, input, ...rows);
}
